/**
 * Organizations Page - Organization management (Mobile Responsive)
 */
import { useState, useEffect } from 'react';
import { api, type Organization, type OrganizationMember, type User } from '../lib/api';

export function OrganizationsPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Create Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', description: '' });
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [editError, setEditError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Details Drawer
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // Add Member Modal
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'admin' | 'member'>('member');
  const [addMemberError, setAddMemberError] = useState('');
  const [isAddingMember, setIsAddingMember] = useState(false);

  const loadOrganizations = async () => {
    setIsLoading(true);
    try {
      const result = await api.getOrganizations({ page, limit, search: searchQuery || undefined });
      if (result.success && result.data) {
        setOrganizations(result.data.organizations);
        setTotalPages(result.data.pagination.totalPages);
        setTotal(result.data.pagination.total);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMembers = async (orgId: string) => {
    setIsLoadingMembers(true);
    try {
      const result = await api.getOrganizationMembers(orgId);
      if (result.success && result.data) {
        setMembers(result.data.members);
      }
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const loadUsers = async () => {
    try {
      const result = await api.getUsers();
      if (result.success && result.data) {
        setAllUsers(result.data.users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, [page, limit, searchQuery]);

  useEffect(() => {
    if (selectedOrg) {
      loadMembers(selectedOrg.id);
    }
  }, [selectedOrg]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadOrganizations();
  };

  const handleCreateOrg = async () => {
    if (!newOrg.name || newOrg.name.length < 2) {
      setCreateError('Name must be at least 2 characters');
      return;
    }

    setIsCreating(true);
    setCreateError('');

    const result = await api.createOrganization({
      name: newOrg.name,
      description: newOrg.description || undefined,
    });

    if (result.success) {
      setShowCreateModal(false);
      setNewOrg({ name: '', description: '' });
      loadOrganizations();
    } else {
      setCreateError(result.error?.message || 'Failed to create organization');
    }

    setIsCreating(false);
  };

  const handleEditOrg = async () => {
    if (!editOrg) return;

    if (!editOrg.name || editOrg.name.length < 2) {
      setEditError('Name must be at least 2 characters');
      return;
    }

    setIsEditing(true);
    setEditError('');

    const result = await api.updateOrganization(editOrg.id, {
      name: editOrg.name,
      description: editOrg.description || undefined,
    });

    if (result.success) {
      setShowEditModal(false);
      setEditOrg(null);
      loadOrganizations();
      if (selectedOrg?.id === editOrg.id) {
        setSelectedOrg({ ...selectedOrg, ...result.data });
      }
    } else {
      setEditError(result.error?.message || 'Failed to update organization');
    }

    setIsEditing(false);
  };

  const handleDeactivate = async (org: Organization) => {
    if (!confirm(`Are you sure you want to deactivate "${org.name}"? Members will lose access to organization data.`)) {
      return;
    }

    const result = await api.deleteOrganization(org.id);
    if (result.success) {
      loadOrganizations();
      if (selectedOrg?.id === org.id) {
        setSelectedOrg({ ...selectedOrg, isActive: false });
      }
    }
  };

  const handleReactivate = async (org: Organization) => {
    const result = await api.reactivateOrganization(org.id);
    if (result.success) {
      loadOrganizations();
      if (selectedOrg?.id === org.id) {
        setSelectedOrg({ ...selectedOrg, isActive: true });
      }
    }
  };

  const handleOpenAddMember = () => {
    setSelectedUserId('');
    setSelectedRole('member');
    setAddMemberError('');
    loadUsers();
    setShowAddMemberModal(true);
  };

  const handleAddMember = async () => {
    if (!selectedOrg || !selectedUserId) {
      setAddMemberError('Please select a user');
      return;
    }

    // Check if user is already a member
    if (members.some((m) => m.userId === selectedUserId)) {
      setAddMemberError('User is already a member of this organization');
      return;
    }

    setIsAddingMember(true);
    setAddMemberError('');

    const result = await api.addOrganizationMember(selectedOrg.id, {
      userId: selectedUserId,
      role: selectedRole,
    });

    if (result.success) {
      setShowAddMemberModal(false);
      loadMembers(selectedOrg.id);
      loadOrganizations(); // Refresh member count
    } else {
      setAddMemberError(result.error?.message || 'Failed to add member');
    }

    setIsAddingMember(false);
  };

  const handleUpdateMemberRole = async (userId: string, newRole: 'admin' | 'member') => {
    if (!selectedOrg) return;

    const result = await api.updateOrganizationMemberRole(selectedOrg.id, userId, newRole);
    if (result.success) {
      loadMembers(selectedOrg.id);
    }
  };

  const handleRemoveMember = async (userId: string, username: string) => {
    if (!selectedOrg) return;

    if (!confirm(`Are you sure you want to remove "${username}" from this organization?`)) {
      return;
    }

    const result = await api.removeOrganizationMember(selectedOrg.id, userId);
    if (result.success) {
      loadMembers(selectedOrg.id);
      loadOrganizations(); // Refresh member count
    } else {
      alert(result.error?.message || 'Failed to remove member');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter out users who are already members for the add member dropdown
  const availableUsers = allUsers.filter(
    (user) => !members.some((m) => m.userId === user.id)
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-8 gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Organizations</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
        >
          + Create Organization
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <form onSubmit={handleSearch} className="flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search organizations..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </form>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
        </select>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Organizations List */}
        <div className={`flex-1 ${selectedOrg ? 'lg:max-w-[60%]' : ''}`}>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {isLoading ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading...</div>
            ) : organizations.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                {searchQuery ? 'No organizations found matching your search' : 'No organizations yet. Create your first one!'}
              </div>
            ) : (
              organizations.map((org) => (
                <div
                  key={org.id}
                  className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-colors ${
                    selectedOrg?.id === org.id ? 'ring-2 ring-blue-500' : ''
                  } ${!org.isActive ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedOrg(org)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500">{org.memberCount} members</div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        org.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {org.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {org.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{org.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t gap-2">
                    <span className="text-xs text-gray-500">{formatDate(org.createdAt)}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditOrg(org);
                          setShowEditModal(true);
                        }}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      {org.isActive ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeactivate(org);
                          }}
                          className="text-sm font-medium text-red-600 hover:text-red-800"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReactivate(org);
                          }}
                          className="text-sm font-medium text-green-600 hover:text-green-800"
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : organizations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchQuery ? 'No organizations found matching your search' : 'No organizations yet. Create your first one!'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Members
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {organizations.map((org) => (
                      <tr
                        key={org.id}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          selectedOrg?.id === org.id ? 'bg-blue-50' : ''
                        } ${!org.isActive ? 'opacity-60' : ''}`}
                        onClick={() => setSelectedOrg(org)}
                      >
                        <td className="px-4 lg:px-6 py-4 font-medium text-gray-900">
                          {org.name}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-gray-500 max-w-xs truncate">
                          {org.description || '-'}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-gray-500">
                          {org.memberCount}
                        </td>
                        <td className="px-4 lg:px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              org.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {org.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-gray-500 whitespace-nowrap">
                          {formatDate(org.createdAt)}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-right whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditOrg(org);
                              setShowEditModal(true);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800 mr-3"
                          >
                            Edit
                          </button>
                          {org.isActive ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeactivate(org);
                              }}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReactivate(org);
                              }}
                              className="text-sm text-green-600 hover:text-green-800"
                            >
                              Reactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <span className="text-sm text-gray-500">
                Showing {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Details Drawer */}
        {selectedOrg && (
          <div className="lg:w-[40%] bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{selectedOrg.name}</h2>
              <button
                onClick={() => setSelectedOrg(null)}
                className="text-gray-400 hover:text-gray-600 lg:hidden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedOrg.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {selectedOrg.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {selectedOrg.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                  <p className="text-gray-700">{selectedOrg.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="text-gray-900">{formatDate(selectedOrg.createdAt)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Updated:</span>
                  <p className="text-gray-900">{formatDate(selectedOrg.updatedAt)}</p>
                </div>
              </div>
            </div>

            {/* Members Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700">
                  Members ({members.length})
                </h3>
                <button
                  onClick={handleOpenAddMember}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Member
                </button>
              </div>

              {isLoadingMembers ? (
                <div className="text-center text-gray-500 py-4">Loading members...</div>
              ) : members.length === 0 ? (
                <div className="text-center text-gray-500 py-4">No members yet</div>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div
                      key={member.userId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                          {member.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{member.username}</div>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(
                              member.role
                            )}`}
                          >
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </span>
                        </div>
                      </div>

                      {member.role !== 'owner' && (
                        <div className="flex items-center gap-2">
                          <select
                            value={member.role}
                            onChange={(e) =>
                              handleUpdateMemberRole(member.userId, e.target.value as 'admin' | 'member')
                            }
                            className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={() => handleRemoveMember(member.userId, member.username)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove member"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              Create Organization
            </h2>

            {createError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {createError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter organization name"
                  maxLength={100}
                />
                <p className="text-xs text-gray-500 mt-1">2-100 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newOrg.description}
                  onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter organization description (optional)"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewOrg({ name: '', description: '' });
                  setCreateError('');
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrg}
                disabled={isCreating}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isCreating ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && editOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              Edit Organization
            </h2>

            {editError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {editError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editOrg.name}
                  onChange={(e) => setEditOrg({ ...editOrg, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter organization name"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editOrg.description || ''}
                  onChange={(e) => setEditOrg({ ...editOrg, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter organization description (optional)"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditOrg(null);
                  setEditError('');
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditOrg}
                disabled={isEditing}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isEditing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              Add Member to {selectedOrg.name}
            </h2>

            {addMemberError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {addMemberError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select User <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">-- Select a user --</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.role})
                    </option>
                  ))}
                </select>
                {availableUsers.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">All users are already members</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as 'admin' | 'member')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedUserId('');
                  setAddMemberError('');
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={isAddingMember || !selectedUserId}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isAddingMember ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
